import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Tag, Plus, Trash2, Edit2, Percent, Euro, 
  Copy, Calendar, Users, CheckCircle, XCircle 
} from 'lucide-react';
import { format } from 'date-fns';
import { formatApiError } from '../lib/apiError';

const StudioVouchersPage = () => {
  const { user, api } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    usage_type: 'multi_use',
    max_uses: 100,
    applies_to: ['classpass', 'membership', 'single_class'],
    valid_from: '',
    valid_until: '',
    min_purchase_amount: '',
    is_active: true
  });

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const data = await apiGet('/api/studio/vouchers');
      setVouchers(data);
    } catch (e) {
      console.error('Failed to fetch vouchers:', e);
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.usage_type === 'multi_use' ? parseInt(formData.max_uses) || null : null,
        min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };

      if (editingVoucher) {
        await apiPut('/api/studio/vouchers/{voucher_id}', { params: { voucher_id: editingVoucher.id }, body: payload });
        toast.success('Voucher updated');
      } else {
        await apiPost('/api/studio/vouchers', { body: payload });
        toast.success('Voucher created');
      }
      
      setShowDialog(false);
      setEditingVoucher(null);
      resetForm();
      fetchVouchers();
    } catch (e) {
      console.error('Failed to save voucher:', e);
      toast.error(formatApiError(e, 'Failed to save voucher'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) return;
    
    try {
      await apiDelete('/api/studio/vouchers/{voucher_id}', { params: { voucher_id: id } });
      toast.success('Voucher deleted');
      fetchVouchers();
    } catch (e) {
      toast.error('Failed to delete voucher');
    }
  };

  const handleEdit = (voucher) => {
    setEditingVoucher(voucher);
    setFormData({
      code: voucher.code,
      discount_type: voucher.discount_type,
      discount_value: voucher.discount_value,
      usage_type: voucher.usage_type,
      max_uses: voucher.max_uses || 100,
      applies_to: voucher.applies_to,
      valid_from: voucher.valid_from?.split('T')[0] || '',
      valid_until: voucher.valid_until?.split('T')[0] || '',
      min_purchase_amount: voucher.min_purchase_amount || '',
      is_active: voucher.is_active
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      usage_type: 'multi_use',
      max_uses: 100,
      applies_to: ['classpass', 'membership', 'single_class'],
      valid_from: '',
      valid_until: '',
      min_purchase_amount: '',
      is_active: true
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const toggleAppliesTo = (type) => {
    setFormData(prev => ({
      ...prev,
      applies_to: prev.applies_to.includes(type)
        ? prev.applies_to.filter(t => t !== type)
        : [...prev.applies_to, type]
    }));
  };

  const getDiscountDisplay = (voucher) => {
    if (voucher.discount_type === 'percentage') {
      return `${voucher.discount_value}% off`;
    }
    return `€${voucher.discount_value.toFixed(2)} off`;
  };

  const isVoucherValid = (voucher) => {
    const now = new Date();
    if (voucher.valid_from && new Date(voucher.valid_from) > now) return false;
    if (voucher.valid_until && new Date(voucher.valid_until) < now) return false;
    if (voucher.usage_type === 'single_use' && voucher.current_uses >= 1) return false;
    if (voucher.max_uses && voucher.current_uses >= voucher.max_uses) return false;
    return voucher.is_active;
  };

  // Bug R24 fix (Iter-62): differentiate "Scheduled" (future valid_from)
  // from "Expired" (past valid_until) so a voucher scheduled for a future
  // activation date no longer displays as "Expired/Used".
  const getVoucherStatusLabel = (voucher) => {
    if (!voucher.is_active) return 'Disabled';
    const now = new Date();
    if (voucher.valid_from && new Date(voucher.valid_from) > now) return 'Scheduled';
    if (voucher.valid_until && new Date(voucher.valid_until) < now) return 'Expired';
    if (voucher.usage_type === 'single_use' && voucher.current_uses >= 1) return 'Used';
    if (voucher.max_uses && voucher.current_uses >= voucher.max_uses) return 'Used';
    return 'Inactive';
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Voucher Codes</h1>
          <p className="text-muted-foreground">Create and manage discount codes for your customers</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingVoucher(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Voucher
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingVoucher ? 'Edit Voucher' : 'Create Voucher'}</DialogTitle>
              <DialogDescription>
                {editingVoucher ? 'Update your voucher code settings' : 'Create a new discount code for your customers'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Voucher Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SUMMER20"
                  required
                  disabled={!!editingVoucher}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed_amount">Fixed Amount (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Discount Value</Label>
                  <div className="relative">
                    {formData.discount_type === 'percentage' ? (
                      <Percent className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Euro className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    )}
                    <Input
                      type="number"
                      min="0"
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Usage Type</Label>
                  <Select
                    value={formData.usage_type}
                    onValueChange={(v) => setFormData({ ...formData, usage_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_use">Single Use</SelectItem>
                      <SelectItem value="multi_use">Multi-Use</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.usage_type === 'multi_use' && (
                  <div className="space-y-2">
                    <Label>Max Uses</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_uses}
                      onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                      placeholder="Unlimited"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Applies To</Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 'classpass', label: 'ClassPasses' },
                    { value: 'membership', label: 'Memberships' },
                    { value: 'single_class', label: 'Single Classes' }
                  ].map(({ value, label }) => (
                    <div key={value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`applies-${value}`}
                        checked={formData.applies_to.includes(value)}
                        onCheckedChange={() => toggleAppliesTo(value)}
                      />
                      <Label htmlFor={`applies-${value}`} className="text-sm font-normal">
                        {label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valid From (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Min Purchase Amount (Optional)</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_purchase_amount}
                    onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                    className="pl-10"
                    placeholder="No minimum"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingVoucher ? 'Update' : 'Create'} Voucher
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading vouchers...</div>
      ) : vouchers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No vouchers yet</h3>
            <p className="text-muted-foreground mb-4">Create your first voucher code to offer discounts to customers</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Voucher
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {vouchers.map((voucher) => (
            <Card key={voucher.id} className={!voucher.is_active ? 'opacity-60' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="font-mono text-lg">{voucher.code}</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => copyCode(voucher.code)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      {voucher.discount_type === 'percentage' ? (
                        <Percent className="w-3 h-3" />
                      ) : (
                        <Euro className="w-3 h-3" />
                      )}
                      {getDiscountDisplay(voucher)}
                    </CardDescription>
                  </div>
                  {isVoucherValid(voucher) ? (
                    <Badge variant="default" className="bg-green-500" data-testid="voucher-status-badge">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className={getVoucherStatusLabel(voucher) === 'Scheduled' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''}
                      data-testid="voucher-status-badge"
                    >
                      {getVoucherStatusLabel(voucher) === 'Scheduled' ? (
                        <Calendar className="w-3 h-3 mr-1" />
                      ) : (
                        <XCircle className="w-3 h-3 mr-1" />
                      )}
                      {getVoucherStatusLabel(voucher)}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>
                    {voucher.current_uses} / {voucher.usage_type === 'single_use' ? '1' : voucher.max_uses || '∞'} uses
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {voucher.applies_to.map((type) => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type === 'classpass' ? 'ClassPass' : type === 'membership' ? 'Membership' : 'Single Class'}
                    </Badge>
                  ))}
                </div>

                {(voucher.valid_from || voucher.valid_until) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {voucher.valid_from ? format(new Date(voucher.valid_from), 'MMM d, yyyy') : 'Any time'}
                      {' → '}
                      {voucher.valid_until ? format(new Date(voucher.valid_until), 'MMM d, yyyy') : 'No end'}
                    </span>
                  </div>
                )}

                {voucher.min_purchase_amount && (
                  <div className="text-sm text-muted-foreground">
                    Min. purchase: €{voucher.min_purchase_amount.toFixed(2)}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(voucher)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(voucher.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default StudioVouchersPage;
